import { useMemo } from 'react';
import type { SearchParamsValue } from '@shared/components/SearchParamsObserver/SearchParamsObserver';
import { useStaysSearchParams } from '@shared/components/SearchParamsObserver/SearchParamsObserver';
import { formatDateComponentsToString } from '@shared/utils/dateHelpers';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';

const IS_SKIP_IF_NO_SEARCH_PARAMS = 'skipIfNoSearchParams';

/**
 * Custom hook to handle URL parameters for hotel booking redirects
 * Manages search params state and generates redirect overrides for dates and occupancy
 */
export const useHotelBookingRedirectOverrides = () => {
  const queryParamHelper = useQueryParamHelper();

  // Parse the URL synchronously instead of waiting on SearchParamsObserver's effect-driven state.
  // That async lag made `skipIfNotValidSearchParams` — and therefore the pricing query's Apollo `skip` —
  // flip true→false a render after mount. On Apollo Client 3.4.x a `skip` true→false toggle, combined with
  // the variable changes from auth/geo metadata resolving mid-load, can leave `useQuery` stuck in
  // `loading: true` with the committed response never delivered (the bug seen only when `skipIfNoSearchParams`
  // is present). Reading the params synchronously makes `skip` correct on the first render so it never
  // toggles: the query fires once and commits normally.
  const searchParams = useStaysSearchParams();

  // Retained so consumers can keep passing it to <SearchParamsObserver onValueUpdate=…>; params are now read
  // synchronously above, so no state plumbing is needed.
  const handleSearchParamsUpdate = useEventCallback((_value: SearchParamsValue) => {});

  const redirectOverrides = useMemo(() => {
    return {
      dates:
        searchParams.startDate.valid && searchParams.endDate.valid
          ? {
              checkIn: formatDateComponentsToString(searchParams.startDate.value),
              checkOut: formatDateComponentsToString(searchParams.endDate.value),
            }
          : undefined,
      occupancies: searchParams.rooms.valid
        ? {
            occupancies: searchParams.rooms.value.map(room => ({
              adults: room.adults,
              childrenAges: room.childrenAges,
            })),
            unitCount: searchParams.rooms.value.length,
          }
        : undefined,
    };
  }, [searchParams]);

  // We should skip the API call when the search params are invalid and the skipIfNoSearchParams query param is set to true. This is needed because some redirects include search params while others don’t. The API should only be triggered once the search params are valid for those cases that include them.
  const skipIfNotValidSearchParams =
    (!searchParams.startDate.valid || !searchParams.endDate.valid || !searchParams.rooms.valid) && queryParamHelper.getValueString(IS_SKIP_IF_NO_SEARCH_PARAMS) === 'true';

  return {
    searchParams,
    handleSearchParamsUpdate,
    redirectOverrides,
    skipIfNotValidSearchParams,
  };
};
